import { useContext, useMemo, useState } from 'react'

import { faUserGraduate, faKey, faEdit } from '@fortawesome/pro-solid-svg-icons'
import Cookie from 'js-cookie'
import { useSelector } from 'react-redux'
import { Select, Tag } from 'antd'

import DataTable from '../../../../Components/antd/Tables/DataTable/DataTable'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import {
  getEmail, getEuropeanBirthDate, getFirstName, getLastLogin, getLastName, getMatricule, getNiss, getPhoneNumber, getStatus, getStudentFullSection,
  getTranslatedGender, getTranslatedLanguage, getTranslatedNationality, Student, StudentProperties as _S, StudentStatusTypes, StudentStatusLabels
} from '../../../../utils/entities/student'
import { OrderBy } from '../../../../utils/constants'
import { isFunction } from 'lodash'
import { Action, DeleteAction } from '../../../../Components/antd/Tables/DataTable/utils/actions'
import { Filter } from '../../../../Components/antd/Tables/DataTable/utils/tableFilter'
import ComponentWithIconLabel from '../../../../Components/icons/ComponentWithIconLabel'
import StudentInfo from '../../../../Components/shared/StudentInfo'
import AdminResetPassword from '../../../../Components/shared/AdminResetPassword'
import { StudentFormModalContext } from '../../../../Providers/Forms/StudentForm/StudentFormModal'
import { Actions } from '../../../../utils/http'
import { selectUser } from '../../../../reducers/UserReducer'

export const StatusTagStyles = Object.freeze({
  [StudentStatusTypes.GRADUATED]: { backgroundColor: 'transparent', borderColor: '#ff4d4f', color: '#ff4d4f', borderRadius: '8px', fontWeight: 500 },
  [StudentStatusTypes.CURRENTLY_STUDYING]: { backgroundColor: 'transparent', borderColor: '#52c41a', color: '#52c41a', borderRadius: '8px', fontWeight: 500 },
  [StudentStatusTypes.DROPPED_OUT]: { backgroundColor: 'transparent', borderColor: '#d4b106', color: '#d4b106', borderRadius: '8px', fontWeight: 500 },
  DEFAULT: { backgroundColor: 'transparent', borderColor: '#8c8c8c', color: '#8c8c8c', borderRadius: '8px', fontWeight: 500 }
})

const Modes = Object.freeze({
  OPEN_SHEET: 1,
  RESET_PASSWORD: 2
})

const StudentsTable = ({ canResetPassword, canSeeAllInformations, data, exporter, loading, parameters, storageKey, onParametersChange, onChange, onDelete, onKeysChanged }) => {
  const { setFormHandler } = useContext(StudentFormModalContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [selectedStudent, setSelectedStudent] = useState({ data: null, mode: null })

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '240px' : '199px'})`, [Cookie.get('switch_user_id')])

  const selectedStatuses = useMemo(() => parameters?.filters?.status ?? [], [parameters?.filters?.status])

  const statusOptions = useMemo(() => StudentStatusLabels.map(status => ({ value: status, label: t('student_status_codes.' + status.toLowerCase()) })), [t])

  const columns = useMemo(() => {
    const columns = [
      { title: t('common.section'), orderBy: OrderBy.SECTIONS, key: s => getStudentFullSection(s, user.school), id: _S.SECTIONS },
      { title: t('common.lastname'), orderBy: OrderBy.LASTNAME, key: getLastName, id: _S.LASTNAME },
      { title: t('common.firstname'), orderBy: OrderBy.FIRSTNAME, key: getFirstName, id: _S.FIRSTNAME },
      { title: t('common.language'), orderBy: OrderBy.LANGUAGE, key: s => getTranslatedLanguage(s, t), id: _S.LANGUAGE },
      { title: t('common.email'), orderBy: OrderBy.EMAIL, key: getEmail, id: _S.EMAIL },
      { title: t('common.matricule'), orderBy: OrderBy.NISS, key: getMatricule, id: _S.MATRICULE },
      { title: t('common.phone_number'), orderBy: OrderBy.PHONE_NUMBER, key: getPhoneNumber, id: _S.PHONE_NUMBER },
      { title: t('common.birthdate'), orderBy: OrderBy.BIRTHDATE, key: getEuropeanBirthDate, id: _S.BIRTHDATE },
      { title: t('common.gender'), orderBy: OrderBy.GENDER, key: s => getTranslatedGender(s, t), id: _S.GENDER },
      { title: t('common.nationality'), orderBy: OrderBy.NATIONALITY, key: s => getTranslatedNationality(s, t), id: _S.NATIONALITY }
    ]

    if (canSeeAllInformations) {
      columns.push({ title: t('common.niss'), orderBy: OrderBy.NISS, key: getNiss, id: _S.NISS })
      columns.push({ title: t('common.last_login'), orderBy: OrderBy.LAST_LOGIN, key: getLastLogin, id: 'user.lastLogin' })
      columns.push({
        title: t('common.status'),
        key: s => {
          const status = getStatus(s)
          const style = StatusTagStyles[status] ?? StatusTagStyles.DEFAULT

          return <Tag style={style}> {t('student_status_codes.' + status.toLowerCase())} </Tag>
        },
        id: 'status'
      })
    }

    return columns
  }, [t, canSeeAllInformations, user.school])

  const actions = useMemo(() => [
    new Action({
      title: 'students_table.actions.display_student_sheet',
      icon: faUserGraduate,
      onClick: i => setSelectedStudent({ data: i, mode: Modes.OPEN_SHEET })
    }),
    new Action({
      disabled: !canResetPassword,
      title: 'students_table.actions.reset_password',
      icon: faKey,
      onClick: i => setSelectedStudent({ data: i, mode: Modes.RESET_PASSWORD })
    }),
    new Action({
      title: 'common.edit',
      icon: faEdit,
      onClick: s => setFormHandler(form => ({ ...form, data: s, callback: s => onChange(s, Actions.UPDATE) }))
    }),
    new DeleteAction({
      disabled: !isFunction(onDelete),
      popconfirmTitle: 'students_table.actions.delete_student',
      onDelete: onDelete
    })
  ], [onDelete, onChange, canResetPassword])

  const tableFilters = useMemo(() => {
    if (!canSeeAllInformations) return []

    return [
      new Filter({
        key: 'status',
        label: t('misc.filters'),
        props: {
          value: selectedStatuses.length > 0 ? selectedStatuses : [StudentStatusTypes.CURRENTLY_STUDYING]
        },
        component: ({ value, onChange }) => (
          <ComponentWithIconLabel icon={faUserGraduate} label={t('common.status')}>
            <Select mode='multiple' style={{ width: '300px' }} value={value} onChange={onChange}>
              {statusOptions.map(option => (
                <Select.Option key={option.value} value={option.value}> {option.label} </Select.Option>
              ))}
            </Select>
          </ComponentWithIconLabel>
        )
      })
    ]
  }, [canSeeAllInformations, selectedStatuses, statusOptions, t])

  const handleClose = () => setSelectedStudent({ data: null, mode: null })

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        data={data}
        exporter={exporter}
        metadata={parameters.metadata}
        loading={loading}
        columns={columns}
        actions={actions}
        filters={tableFilters}
        parameters={parameters}
        storageKey={storageKey}
        onCreate={() => setFormHandler(form => ({ ...form, data: new Student(), callback: onChange }))}
        onKeysChanged={onKeysChanged}
        onParametersChange={onParametersChange}
      />
      <StudentInfo student={selectedStudent.mode === Modes.OPEN_SHEET ? selectedStudent.data : null} onClose={handleClose} />
      {canResetPassword && (
        <AdminResetPassword
          userToResetPassword={selectedStudent.mode === Modes.RESET_PASSWORD ? selectedStudent.data : null}
          onClose={handleClose}
        />
      )}
    </div>
  )
}

export default StudentsTable
